import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

interface WorkReport {
  id: string;
  work_type: string;
  worker_count: number;
  work_hours: number;
  region: string;
  site_name: string;
  work_description: string;
  work_date: string;
  weather: string;
  profile?: { name: string } | null;
}

function getPeriodRange(period: "week" | "month" | "year", selectedYear?: number) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (period === "week") {
    const start = new Date(today);
    start.setDate(today.getDate() - 6);
    return {
      start: start.toISOString().slice(0, 10),
      end: today.toISOString().slice(0, 10),
    };
  }

  if (period === "month") {
    return {
      start: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`,
      end: today.toISOString().slice(0, 10),
    };
  }

  const year = selectedYear || now.getFullYear();
  const isCurrentYear = year === now.getFullYear();
  return {
    start: `${year}-01-01`,
    end: isCurrentYear ? today.toISOString().slice(0, 10) : `${year}-12-31`,
  };
}

export default function MonthlyReportPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<"week" | "month" | "year">("month");
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [reports, setReports] = useState<WorkReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const now = new Date();
  const periodLabel = period === "week" ? "주간" : period === "month" ? "월간" : "연간";

  useEffect(() => {
    async function fetchReports() {
      setLoading(true);
      setError("");
      try {
        const data = await api.get<WorkReport[]>("/reports");
        setReports(data);
      } catch {
        setError("리포트 데이터를 불러오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    }

    fetchReports();
  }, []);

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    reports.forEach(r => {
      if (r.work_date) {
        const y = parseInt(r.work_date.slice(0, 4), 10);
        if (!isNaN(y)) years.add(y);
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [reports]);

  const filteredReports = useMemo(() => {
    const range = getPeriodRange(period, selectedYear);
    return reports.filter(r => r.work_date >= range.start && r.work_date <= range.end);
  }, [reports, period, selectedYear]);

  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth() + 1;

  const rangeStr = useMemo(() => {
    const range = getPeriodRange(period, selectedYear);
    if (period === "year") {
      if (selectedYear === nowYear) {
        return `${selectedYear}년 1월 ~ ${nowMonth}월 (오늘 기준)`;
      }
      return `${selectedYear}년 1월 ~ 12월`;
    }
    return `${range.start} ~ ${range.end}`;
  }, [period, selectedYear, nowMonth, nowYear]);

  const totalReports = filteredReports.length;
  const totalHours = filteredReports.reduce((sum, r) => sum + (r.work_hours || 0), 0);
  const totalWorkers = filteredReports.reduce((sum, r) => sum + (r.worker_count || 0), 0);
  const avgWorkersPerDay = totalReports > 0 ? Math.round(totalWorkers / totalReports) : 0;

  const workTypeDistribution = useMemo(() => {
    const map = new Map<string, number>();
    filteredReports.forEach(r => {
      map.set(r.work_type, (map.get(r.work_type) || 0) + 1);
    });
    const total = filteredReports.length || 1;
    return Array.from(map.entries())
      .map(([type, count]) => ({
        type,
        count,
        percentage: Math.round((count / total) * 1000) / 10,
      }))
      .sort((a, b) => b.count - a.count);
  }, [filteredReports]);

  const regionStats = useMemo(() => {
    const map = new Map<string, number>();
    filteredReports.forEach(r => {
      map.set(r.region, (map.get(r.region) || 0) + 1);
    });
    const total = filteredReports.length || 1;
    return Array.from(map.entries())
      .map(([region, count]) => ({
        region,
        count,
        percentage: Math.round((count / total) * 1000) / 10,
      }))
      .sort((a, b) => b.count - a.count);
  }, [filteredReports]);

  const siteStats = useMemo(() => {
    const map = new Map<string, { count: number; hours: number; workers: number }>();
    filteredReports.forEach(r => {
      if (!map.has(r.site_name)) {
        map.set(r.site_name, { count: 0, hours: 0, workers: 0 });
      }
      const entry = map.get(r.site_name)!;
      entry.count += 1;
      entry.hours += r.work_hours || 0;
      entry.workers += r.worker_count || 0;
    });
    return Array.from(map.entries())
      .map(([site, stats]) => ({ site, ...stats }))
      .sort((a, b) => b.count - a.count);
  }, [filteredReports]);

  const recentReports = filteredReports.slice(0, 20);

  const weatherIcons: Record<string, string> = {
    "맑음": "ri-sun-line",
    "흐림": "ri-cloudy-line",
    "비": "ri-showers-line",
    "눈": "ri-snowy-line",
    "강풍": "ri-windy-line",
    "구름조금": "ri-cloudy-2-line"
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">리포트 생성 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-red-400 text-2xl"></i>
        </div>
        <p className="text-sm text-gray-600 mb-3">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #monthly-report, #monthly-report * { visibility: visible; }
          #monthly-report { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
          .no-print { display: none !important; }
          @page { margin: 1.5cm; size: A4; }
        }
      `}</style>

      <div className="no-print mb-6">
        <button onClick={() => navigate("/statistics")} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 cursor-pointer mb-2">
          <i className="ri-arrow-left-line"></i>
          통계로 돌아가기
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{periodLabel} 작업 리포트</h1>
            <p className="text-sm text-gray-400 mt-0.5">{rangeStr} 기준</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-gray-50 rounded-full p-1">
              {([
                { value: "week" as const, label: "주간" },
                { value: "month" as const, label: "월간" },
                { value: "year" as const, label: "연간" }
              ]).map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    period === p.value
                      ? "bg-white text-gray-900"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {period === "year" && availableYears.length > 0 && (
              <div className="relative">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                  className="pl-3 pr-8 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 cursor-pointer bg-white"
                >
                  {availableYears.map(y => (
                    <option key={y} value={y}>{y}년</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
              </div>
            )}
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-printer-line"></i>
              인쇄
            </button>
          </div>
        </div>
      </div>

      <div id="monthly-report" className="bg-white rounded-xl overflow-hidden">
        <div className="p-8 border-b-2 border-amber-500">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">우공이산 작업일보</h1>
              <p className="text-lg text-gray-600 mt-1">{periodLabel} 작업 리포트</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">{rangeStr}</p>
              <p className="text-xs text-gray-400 mt-1">
                생성일: {now.toLocaleDateString("ko-KR")}
              </p>
            </div>
          </div>
        </div>

        <div className="p-8 space-y-8">
          <section>
            <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4">1. 종합 현황</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: "총 작업일보", value: totalReports, unit: "건" },
                { label: "총 작업 시간", value: totalHours.toLocaleString(), unit: "시간" },
                { label: "총 투입 인원", value: totalWorkers.toLocaleString(), unit: "명" },
                { label: "일평균 인원", value: avgWorkersPerDay, unit: "명/일" },
              ].map((item) => (
                <div key={item.label} className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                  <p className="text-xl font-bold text-gray-900">
                    {item.value}
                    <span className="text-sm font-normal text-gray-400 ml-0.5">{item.unit}</span>
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4">2. 작업 유형별 분포</h2>
            {workTypeDistribution.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">해당 기간에 작업 데이터가 없습니다</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">작업 유형</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">건수</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">비율</th>
                      <th className="px-4 py-2.5" style={{ width: "40%" }}></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {workTypeDistribution.map((item) => (
                      <tr key={item.type}>
                        <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{item.type}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{item.count}건</td>
                        <td className="px-4 py-2.5 text-sm text-gray-500 text-right">{item.percentage}%</td>
                        <td className="px-4 py-2.5">
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-amber-400 rounded-full"
                              style={{ width: `${Math.max(item.percentage, 2)}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4">3. 지역별 통계</h2>
            {regionStats.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">해당 기간에 작업 데이터가 없습니다</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">지역</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">건수</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">비율</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {regionStats.map((item, index) => (
                      <tr key={item.region} className={index % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                        <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{item.region}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{item.count}건</td>
                        <td className="px-4 py-2.5 text-sm text-gray-500 text-right">{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4">4. 현장별 현황</h2>
            {siteStats.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">해당 기간에 작업 데이터가 없습니다</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">현장명</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">일보 건수</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">작업 시간</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">투입 인원</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {siteStats.map((item, index) => (
                      <tr key={item.site} className={index % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                        <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{item.site}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{item.count}건</td>
                        <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{item.hours}h</td>
                        <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{item.workers}명</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4">5. 최근 작업 내역</h2>
            {recentReports.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">해당 기간에 작업 데이터가 없습니다</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">일자</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">현장명</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">유형</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">내용</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">인원</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">시간</th>
                      <th className="text-center px-4 py-2.5 text-xs font-medium text-gray-500">날씨</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recentReports.map((r, index) => (
                      <tr key={r.id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                        <td className="px-4 py-2.5 text-sm text-gray-900 whitespace-nowrap">{r.work_date}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-900 max-w-[140px] truncate">{r.site_name}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">{r.work_type}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-500 max-w-[200px] truncate">{r.work_description}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-600 text-right whitespace-nowrap">{r.worker_count}명</td>
                        <td className="px-4 py-2.5 text-sm text-gray-600 text-right whitespace-nowrap">{r.work_hours}h</td>
                        <td className="px-4 py-2.5 text-center whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <i className={`${weatherIcons[r.weather] || "ri-cloud-line"} text-sm`}></i>
                            {r.weather}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <div className="border-t border-gray-200 pt-6 text-center text-xs text-gray-400">
            <p>우공이산 작업일보 시스템 - 자동 생성된 {periodLabel} 리포트</p>
            <p className="mt-1">본 리포트는 {rangeStr} 기간의 작업 데이터를 기반으로 작성되었습니다.</p>
          </div>
        </div>
      </div>
    </div>
  );
}